import { useState } from 'react';
import { LISTING_STATUSES, CURRENCIES, type ListingStatus, type Currency } from '@siliconvault/shared';
import { useUpdateListingStatus } from '../../hooks/useCommunity';
import { useToast } from '../ui/Toast';
import { useTranslation } from '../../i18n';
import styles from './ListingStatusSelector.module.css';

interface Props {
  itemId: string;
  currentStatus: string;
  currentPrice: string | null;
  currentCurrency: string;
}

export function ListingStatusSelector({ itemId, currentStatus, currentPrice, currentCurrency }: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const mutation = useUpdateListingStatus();
  const [askingPrice, setAskingPrice] = useState(currentPrice ?? '');
  const [currency, setCurrency] = useState<Currency>(currentCurrency as Currency);

  const statusConfig: Record<string, { label: string; variant: string }> = {
    personal: { label: t('community.statusPersonal'), variant: styles.toggleOptionPersonal },
    'for-trade': { label: t('community.statusForTrade'), variant: styles.toggleOptionTrade },
    'for-sale': { label: t('community.statusForSale'), variant: styles.toggleOptionSale },
  };

  async function handleStatusChange(status: ListingStatus) {
    try {
      await mutation.mutateAsync({
        itemId,
        data: {
          listingStatus: status,
          askingPrice: status === 'for-sale' && askingPrice ? askingPrice : null,
          listingCurrency: currency,
        },
      });
      toast(t('community.listingUpdated'), 'success');
    } catch (err: any) {
      toast(err?.message || t('community.listingUpdateFailed'), 'error');
    }
  }

  async function handlePriceSave() {
    if (currentStatus !== 'for-sale') return;
    try {
      await mutation.mutateAsync({
        itemId,
        data: {
          listingStatus: currentStatus as ListingStatus,
          askingPrice: askingPrice || null,
          listingCurrency: currency,
        },
      });
      toast(t('community.listingUpdated'), 'success');
    } catch (err: any) {
      toast(err?.message || t('community.listingUpdateFailed'), 'error');
    }
  }

  return (
    <div className={styles.selector}>
      <div className={styles.toggleGroup}>
        {LISTING_STATUSES.map((s) => {
          const cfg = statusConfig[s];
          const isActive = currentStatus === s;
          return (
            <button
              key={s}
              className={`${styles.toggleOption} ${cfg.variant} ${isActive ? styles.active : ''}`}
              onClick={() => handleStatusChange(s)}
              disabled={mutation.isPending}
            >
              <span className={styles.statusDot} />
              {cfg.label}
            </button>
          );
        })}
      </div>

      {currentStatus === 'for-sale' && (
        <div className={styles.priceGroup}>
          <div className={styles.priceInputWrap}>
            <select
              className={styles.priceCurrency}
              value={currency}
              onChange={(e) => { setCurrency(e.target.value as Currency); }}
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <input
              className={styles.priceInput}
              type="text"
              value={askingPrice}
              onChange={(e) => setAskingPrice(e.target.value)}
              placeholder={t('community.askingPrice')}
              onBlur={handlePriceSave}
            />
          </div>
        </div>
      )}
    </div>
  );
}
